/**
 * Whether what a presentation step shows has actually painted.
 *
 * Split from `agent-presentation-ack.ts`, which reached the line limit: that
 * file tells the companion a step was seen; this one decides whether it was.
 */
import { isWindowsPlatform } from "@/lib/os-platform";
import { findMountedFrameElement } from "@/desktop/frame-presentation-measurement";
import { waitForBrowserPaint } from "@/lib/browser-paint";
import type {
  AgentPresentation,
  AgentPresentationStep,
} from "@/lib/agent-presentation-types";
import { useCanvasStore } from "@/stores/canvas-store";
import { usePresentationStore } from "@/stores/presentation-store";
import { isAgentFrame } from "@/lib/focus/focus-plan-rounds";
import { focusOwnerKey } from "@/lib/agent-focus-origin";
import { CANVAS_VIEWPORT_TRANSITION_MS } from "@/lib/canvas-viewport-transitions";

const FRAME_PAINT_TIMEOUT_MS =
  Math.max(
    CANVAS_VIEWPORT_TRANSITION_MS.focusReveal,
    CANVAS_VIEWPORT_TRANSITION_MS.panToFrame,
  ) + 500;
const FRAME_PAINT_ATTEMPT_LIMIT = 600;
const CURSOR_PAINT_TIMEOUT_MS = 1_200;
const CURSOR_PAINT_ATTEMPT_LIMIT = 300;

function visible(element: Element | null): boolean {
  if (!element) return false;
  const rect = element.getBoundingClientRect();
  if (
    !(
      rect.width > 0 &&
      rect.height > 0 &&
      rect.right > 0 &&
      rect.bottom > 0 &&
      rect.left < window.innerWidth &&
      rect.top < window.innerHeight
    )
  ) {
    return false;
  }
  for (
    let current: Element | null = element;
    current;
    current = current.parentElement
  ) {
    const style = getComputedStyle(current);
    if (
      style.display === "none" ||
      style.visibility === "hidden" ||
      style.opacity === "0"
    ) {
      return false;
    }
  }
  return true;
}

function visibleTestElements(
  ids: string[],
  root: ParentNode = document,
): HTMLElement[] {
  const elements = [...root.querySelectorAll<HTMLElement>("[data-testid]")];
  return ids.flatMap((testId) =>
    elements.filter(
      (candidate) => candidate.dataset.testid === testId && visible(candidate),
    ),
  );
}

function testElements(
  ids: string[],
  root: ParentNode = document,
): HTMLElement[] {
  const elements = [...root.querySelectorAll<HTMLElement>("[data-testid]")];
  return ids.flatMap((testId) =>
    elements.filter((candidate) => candidate.dataset.testid === testId),
  );
}

export function visibleStepAnnotation(
  presentation: AgentPresentation,
  step: AgentPresentationStep,
): HTMLElement | null {
  const annotationId = `${presentation.presentationId}:${step.step_id}`;
  // Delivery identity survives mirrored current/retained target snapshots.
  return (
    [...document.querySelectorAll<HTMLElement>("[data-annotation-id]")].find(
      (candidate) =>
        candidate.dataset.annotationId === annotationId && visible(candidate),
    ) ?? null
  );
}

function bubbleContainsRequestedCopy(
  bubble: HTMLElement | null,
  step: Exclude<AgentPresentationStep, { kind: "pause" | "clear" }>,
): boolean {
  const requestedCopy = [step.label, step.bubble]
    .map((value) => value?.trim())
    .filter((value): value is string => Boolean(value));
  if (requestedCopy.length === 0) return true;
  const sourceCopy = bubble?.closest<HTMLElement>("[data-cursor-reply]")
    ?.dataset.cursorReply;
  const paintedCopy = bubble?.textContent?.trim()
    ? (sourceCopy ?? bubble.textContent).replace(/\s+/g, " ").trim()
    : "";
  return requestedCopy.every((copy) =>
    paintedCopy.includes(copy.replace(/\s+/g, " ")),
  );
}

function cursorAndBubbleAreVisible(
  presentation: AgentPresentation,
  step: Exclude<AgentPresentationStep, { kind: "pause" | "clear" }>,
): boolean {
  const cursorId = presentation.agentId?.trim() || "denker-present-cursor";
  const cursorCandidates = [
    {
      cursorIds: [`agent-cursor-${cursorId}`],
      bubbleIds: [`agent-cursor-${cursorId}-bubble`],
    },
    {
      cursorIds: ["desktop-agent-cursor"],
      bubbleIds: ["agent-cursor-bubble"],
    },
    {
      cursorIds: ["desktop-agent-cursor-guidance"],
      bubbleIds: ["agent-cursor-bubble"],
    },
  ];
  return cursorCandidates.some((candidate) =>
    (() => {
      const cursors = testElements(candidate.cursorIds);
      const versioned = cursors.filter((cursor) =>
        Boolean(cursor.dataset.presentationDeliveryId),
      );
      const exact = cursors.filter(
        (cursor) =>
          cursor.dataset.presentationDeliveryId === presentation.presentationId,
      );
      return versioned.length > 0 ? exact : cursors;
    })().some((cursor) => {
      const bubble =
        visibleTestElements(candidate.bubbleIds, cursor)[0] ?? null;
      if (!bubbleContainsRequestedCopy(bubble, step)) return false;
      return (
        visible(cursor) ||
        bubble !== null ||
        [...cursor.querySelectorAll<HTMLElement>("*")].some(visible)
      );
    }),
  );
}

function visibleStepCallout(
  presentation: AgentPresentation,
  step: AgentPresentationStep,
): HTMLElement | null {
  const annotationId = `${presentation.presentationId}:${step.step_id}`;
  for (const anchor of document.querySelectorAll<HTMLElement>(
    "[data-annotation-callout-id]",
  )) {
    if (anchor.dataset.annotationCalloutId !== annotationId) continue;
    const bubble = anchor.querySelector<HTMLElement>(
      '[data-agent-annotation-callout="true"]',
    );
    if (visible(bubble) && bubble?.textContent?.trim()) return bubble;
  }
  return null;
}

function cursorVisualIsVisible(
  presentation: AgentPresentation,
  step: AgentPresentationStep,
): boolean {
  if (step.kind === "pause" || step.kind === "clear" || step.kind === "frame") {
    return true;
  }
  if (["highlight", "draw", "drag"].includes(step.kind)) {
    if (visibleStepAnnotation(presentation, step) == null) return false;
    const callout = visibleStepCallout(presentation, step);
    if (
      (step.kind === "highlight" || step.kind === "draw") &&
      step.callout_role === "answer"
    ) {
      const copy = step.callout?.trim().replace(/\s+/g, " ");
      const calloutPainted =
        copy &&
        callout?.dataset.cursorReply?.replace(/\s+/g, " ").trim() === copy;
      if (
        !calloutPainted &&
        !cursorAndBubbleAreVisible(presentation, {
          ...step,
          label: undefined,
          bubble: step.callout,
        })
      )
        return false;
    }
    const requestsBubble = Boolean(step.label?.trim() || step.bubble?.trim());
    return (
      !requestsBubble ||
      (callout !== null && bubbleContainsRequestedCopy(callout, step)) ||
      cursorAndBubbleAreVisible(presentation, step)
    );
  }
  return cursorAndBubbleAreVisible(presentation, step);
}

/** Why a step's paint check failed, named after the part that did not show. */
export function paintFailureReason(
  presentation: AgentPresentation,
  step: AgentPresentationStep,
): string {
  if (step.kind === "frame") {
    return framesAreVisible(step) ? "bubble_not_visible" : "frame_not_visible";
  }
  if (step.kind === "clear") return "clear_not_applied";
  if (
    ["highlight", "draw", "drag"].includes(step.kind) &&
    !visibleStepAnnotation(presentation, step)
  ) {
    return "annotation_not_visible";
  }
  return "cursor_or_bubble_not_visible";
}

function framesAreVisible(
  step: Extract<AgentPresentationStep, { kind: "frame" }>,
): boolean {
  return step.frame_ids.every((id) => visible(findMountedFrameElement(id)));
}

function frameVisualIsVisible(
  presentation: AgentPresentation,
  step: Extract<AgentPresentationStep, { kind: "frame" }>,
): boolean {
  if (!framesAreVisible(step)) return false;
  if (!step.label?.trim() && !step.bubble?.trim()) return true;
  return cursorAndBubbleAreVisible(presentation, step);
}

function clearIsApplied(
  presentation: AgentPresentation,
  step: Extract<AgentPresentationStep, { kind: "clear" }>,
): boolean {
  const state = useCanvasStore.getState();
  const targets = new Set(step.targets ?? ["cursor", "annotations", "frames"]);
  const presentedCursors = [...state.agentCursors.values()].filter(
    (cursor) =>
      cursor.presentation === "point" &&
      (presentation.projectId
        ? cursor.projectId === presentation.projectId &&
          (!presentation.executionId ||
            cursor.executionId === presentation.executionId)
        : !presentation.spaceId || cursor.spaceId === presentation.spaceId),
  );
  if (targets.has("cursor") && presentedCursors.length > 0) return false;
  if (
    targets.has("annotations") &&
    presentedCursors.some(
      (cursor) =>
        cursor.annotationTarget ||
        cursor.annotationTargets?.length ||
        cursor.annotationExclusionTargets,
    )
  ) {
    return false;
  }
  const focus = usePresentationStore.getState().presentation;
  return (
    !targets.has("frames") ||
    !focus?.frameIds.some((id) =>
      isAgentFrame(focus, id, focusOwnerKey(presentation)),
    )
  );
}

export async function visualIsPainted(
  presentation: AgentPresentation,
  step: AgentPresentationStep,
  signal: AbortSignal,
  attemptLimit?: number,
): Promise<boolean> {
  // Bound paint checks by viewport transition time, not display refresh rate.
  const paintDeadline =
    Date.now() +
    (step.kind === "frame" ? FRAME_PAINT_TIMEOUT_MS : CURSOR_PAINT_TIMEOUT_MS);
  const maxPaintAttempts =
    attemptLimit ??
    (step.kind === "frame"
      ? FRAME_PAINT_ATTEMPT_LIMIT
      : CURSOR_PAINT_ATTEMPT_LIMIT);
  for (let attempt = 0; attempt < maxPaintAttempts; attempt += 1) {
    // A slow frame is no failure: the step keeps its whole budget.
    await waitForBrowserPaint(signal);
    if (signal.aborted) return false;
    if (
      step.kind === "frame"
        ? frameVisualIsVisible(presentation, step)
        : step.kind === "clear"
          ? clearIsApplied(presentation, step)
          : cursorVisualIsVisible(presentation, step)
    ) {
      return true;
    }
    if (Date.now() >= paintDeadline) return false;
  }
  return false;
}

export function performedByTheApplication(
  presentation: AgentPresentation,
  step: AgentPresentationStep,
): boolean {
  return (
    presentation.mode === "operate" &&
    // Not scroll: a region that will not scroll itself falls back to a real
    // wheel at a point. A named paste or a paste at a named window's caret
    // never falls back to the pointer on either platform
    // (`cursor_control_decision::never_uses_the_pointer`), and a key never
    // uses it at all - so none of them can touch anything unseen. Measured on
    // a Mac (2026-10-01): a caret paste into a freshly opened window was
    // refused because the drawn cursor had not painted yet, and the whole
    // 38-row walk stopped before typing anything.
    (step.kind === "key" ||
      (step.kind === "paste" &&
        (step.semantic_target !== undefined || step.window_id !== undefined)) ||
      // A named click is a UI Automation press on Windows; a Mac one can
      // still fall back to the real pointer, so it waits for the paint there.
      (isWindowsPlatform() &&
        step.kind === "click" &&
        step.semantic_target !== undefined))
  );
}
